import './DrawerNewInspection.style.less'
import {
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Select,
  Spin,
  message,
  Row,
  Grid,
} from 'antd'
import { DoubleRightOutlined, CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { find } from 'lodash'
import {
  createInspection,
  getAllInspectors,
  getInspectionTemplatesByInspectionType,
  getInspectionTypes,
} from '@/screens/QSF/Inspection/Inspection.service'
import { InspectionConstant } from '@/screens/QSF/Inspection/Inspection.constant'
import { queryClient } from '@/config/react-query'
import { COMPANY_SETTING_HAS_MEASUREMENT_AQL, USER_ROLE } from '@/constant/user'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import { PBF_QUERY_KEY } from '@/constant/pbf'
import { useAuth } from '@/providers/auth.provider'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT } from '@/constant/date'
import FileUploadField from '@/components/FileUploadField'
import { UPLOAD_DOCUMENTS_ENDPOINT } from '@/constant/common'
import { IFileItem, useFileUpload } from '@/hook/useFileUpload'
import { ISelectedOrderRow } from '@/screens/Order/jotai/order.jotai'
import { SelectionValidationResult } from '../../hooks/useOrderSelectionValidation'
import { isEmptyValue, toJson } from '@/utils/common'

const { Option } = Select

interface AqlOption {
  label: string
  value: string
}

interface Props {
  open: boolean
  selectedRows: ISelectedOrderRow[]
  validation: SelectionValidationResult
  userRole: string
  aqlOptions: AqlOption[]
  measurementAqlOptions: AqlOption[]
  onClose: () => void
  onSuccess: () => void
}

function DrawerNewInspection({
  open,
  selectedRows,
  validation,
  userRole,
  aqlOptions,
  measurementAqlOptions,
  onClose,
  onSuccess,
}: Props) {
  const { user } = useAuth()
  const screens = Grid.useBreakpoint()
  const [form] = Form.useForm()
  const [selectedInspectionType, setSelectedInspectionType] = useState('')

  useEffect(() => {
    if (!open || !selectedRows.length || !aqlOptions?.length) return
    let aql = ''
    let aqlId = ''
    let measurementAql = ''
    let measurementAqlId = ''

    for (const row of selectedRows) {
      const aqlProp: any = (row.property_values || []).find((p: any) => p.field === 'aql')
      if (aqlProp && !aqlId) {
        aql = aqlProp.value || ''
        aqlId = aqlProp.parent?.id || ''
      }

      const mAqlProp: any = (row.property_values || []).find((p: any) => p.field === 'measurement_aql')
      if (mAqlProp && !measurementAqlId) {
        measurementAql = mAqlProp.value || ''
        measurementAqlId = mAqlProp.parent?.id || ''
      }
    }

    const aqlJson = aql ? toJson(aql) : null
    const picked =
      find(
        aqlOptions,
        ({ value, label }) => value === aqlId || label === aqlJson?.name
      ) ?? null

    if (picked) {
      form.setFieldValue('inspection_aql', picked.value)
    } else {
      const defaultAql = aqlOptions.find((opt: any) => opt.is_default)
      if (defaultAql) {
        form.setFieldValue('inspection_aql', defaultAql.value)
      }
    }

    const measurementAqlJson = measurementAql ? toJson(measurementAql) : null
    const pickedMeasurement =
      find(
        measurementAqlOptions,
        ({ value, label }) => value === measurementAqlId || label === measurementAqlJson?.name
      ) ?? null

    if (pickedMeasurement) {
      form.setFieldValue('measurement_aql', pickedMeasurement.value)
    } else {
      const defaultMeasurementAql = measurementAqlOptions.find((opt: any) => opt.is_default)
      if (defaultMeasurementAql) {
        form.setFieldValue('measurement_aql', defaultMeasurementAql.value)
      }
    }
  }, [open, selectedRows, aqlOptions, measurementAqlOptions])

  useEffect(() => {
    if (!open) return
    if (
      userRole === USER_ROLE.INSPECTOR ||
      userRole === USER_ROLE.SUPPLIER
    ) {
      form.setFieldValue('assigned_inspection_id', user?.id)
    }
  }, [open, userRole])

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setSelectedInspectionType('')
      setTemplateHasMeasurement(false)
    }
  }, [open])

  const { data: companySettingMeasurementAql } = useQuery(
    [PBF_QUERY_KEY.GET_COMPANY_SETTING_MEASUREMENT_AQL],
    async () => {
      return await getCompanySetting('', COMPANY_SETTING_HAS_MEASUREMENT_AQL)
    }
  )
  const hasMeasurementAqlSetting = companySettingMeasurementAql?.value?.status



  const queryInspectionType = useQuery(['INSPECTION_TYPES'], async () => {
    const response = await getInspectionTypes()
    return response.data?.data.map((t: any) => ({
      ...t,
      value: toJson(t.value),
    }))
  })

  const queryInspectionTemplate = useQuery(
    ['INSPECTION_TEMPLATES', selectedInspectionType],
    () => getInspectionTemplatesByInspectionType(selectedInspectionType),
    { enabled: !!selectedInspectionType }
  )

  const [templateHasMeasurement, setTemplateHasMeasurement] = useState(false)
  const selectedTemplateId = Form.useWatch('template_id', form)

  useEffect(() => {
    if (!selectedTemplateId || !queryInspectionTemplate.data) {
      setTemplateHasMeasurement(false)
      return
    }
    const templates = queryInspectionTemplate?.data?.data?.data ?? []
    const selectedTemplate = templates.find(
      (t: any) => t.id === selectedTemplateId
    )
    const sections = selectedTemplate?.template_value?.sections ?? []
    const hasMeasurement = sections.some(
      (s: any) => s.options?.type === 'MEASUREMENT_SECTION'
    )
    setTemplateHasMeasurement(hasMeasurement)
    if (!hasMeasurement) {
      form.setFieldValue('measurement_aql', null)
    }
  }, [selectedTemplateId, queryInspectionTemplate.data])

  const queryInspectors = useQuery(['INSPECTORS'], async () =>
    userRole === USER_ROLE.ADMIN ? await getAllInspectors() : []
  )

  const createMutation = useMutation(async (payload: any) =>
    createInspection(payload)
  )

  const { uploadFiles } = useFileUpload({ endpoint: UPLOAD_DOCUMENTS_ENDPOINT })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()
      const documentResult = await uploadFiles(values.documents || [])
      const uploadedIds = documentResult
        .map((d: IFileItem) => d?.id)
        .filter((id: any) => !!id)

      const payload = {
        order_ids: selectedRows.map((r) => r.id),
        has_multi_orders: selectedRows.length > 1,
        inspection_type_id: values.inspection_type_id,
        template_id: values.template_id,
        aql_id: values.inspection_aql,
        measurement_aql_id:
          templateHasMeasurement && hasMeasurementAqlSetting
            ? values.measurement_aql ?? null
            : null,
        planned_date: dateFormat(values.planned_date, DATE_FORMAT.YYYYMMDD),
        assigned_inspection_id:
          userRole === USER_ROLE.ADMIN
            ? values.assigned_inspection_id ?? null
            : user?.id,
        media_ids: uploadedIds,
      }

      const response = await createMutation.mutateAsync(payload)
      if ([200, 201].includes(response.data.statusCode)) {
        const inspectionNumber = response.data?.data?.number ?? ''
        message.success(
          inspectionNumber
            ? `Inspection no. ${inspectionNumber} created.`
            : 'Inspection created.'
        )
        queryClient.refetchQueries(['INSPECTIONS'])
        queryClient.invalidateQueries([
          InspectionConstant.QUERY_KEY.INSPECTIONS_ADVANCE_SUMMARY,
        ])
        onSuccess()
      }
    } catch (error: any) {
      if (error?.errorFields) return
      message.error('Something went wrong. Try again.')
    }
  }

  const selectedCount = selectedRows.length
  const deliveredCount = selectedRows.filter((r) => r.is_delivered === true).length
  const allDelivered = selectedCount > 0 && deliveredCount === selectedCount
  const form_inspection_type_id = Form.useWatch('inspection_type_id', form)
  const form_template_id = Form.useWatch('template_id', form)
  const form_inspection_aql = Form.useWatch('inspection_aql', form)
  const hasAllRequiredFields = !!(form_inspection_type_id && form_template_id && form_inspection_aql)

  const isCreateDisabled = !validation.isValid || createMutation.isLoading || !hasAllRequiredFields || allDelivered

  const renderInspectorField = () => {
    if (
      userRole === USER_ROLE.INSPECTOR ||
      userRole === USER_ROLE.SUPPLIER
    ) {
      return (
        <Form.Item label="Inspector">
          <Select
            disabled
            size="large"
            value={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`}
          />
        </Form.Item>
      )
    }

    return (
      <Form.Item name="assigned_inspection_id" label="Inspector">
        <Select
          allowClear
          showSearch
          size="large"
          placeholder="Select"
          optionFilterProp="children"
          notFoundContent={
            queryInspectors.isLoading ? (
              <Spin size="small" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        >
          {queryInspectors.data?.map((inspector: any) => (
            <Option key={inspector.id} value={inspector.id}>
              {`${inspector.first_name ?? ''} ${inspector.last_name ?? ''}`}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  return (
    <Drawer
      className="drawer-new-inspection"
      title=""
      closable={false}
      placement="right"
      width={screens.xs ? '100%' : 550}
      open={open}
      onClose={onClose}
      mask={false}
      maskClosable={false}
    >
      <Row className="title-container">
        <Row>
          <Button
            onClick={onClose}
            className="btn-back"
            icon={<DoubleRightOutlined className="close-icon" />}
          />
        </Row>
        <Row>
          <h1>New Inspection</h1>
        </Row>
      </Row>
      {selectedCount === 0 ? (
        <div className="drawer-new-inspection__selected-indicator">
          <ExclamationCircleFilled className="warning-icon" />
          <span>0 orders selected</span>
        </div>
      ) : !validation.isValid && !isEmptyValue(validation.tooltipText) ? (
        <div className="drawer-new-inspection__selected-indicator">
          <ExclamationCircleFilled className="error-icon" />
          <span>
            {selectedCount} order{selectedCount !== 1 ? 's' : ''} selected. {validation.tooltipText}
          </span>
        </div>
      ) : validation.warningText ? (
        <div className="drawer-new-inspection__selected-indicator">
          <ExclamationCircleFilled className="warning-icon" />
          <span>{validation.warningText}</span>
        </div>
      ) : (
        <div className="drawer-new-inspection__selected-indicator">
          <CheckCircleFilled className="success-icon" />
          <span>{selectedCount} order{selectedCount !== 1 ? 's' : ''} selected</span>
        </div>
      )}

      <div className="wrap-filter-form">
        <Form form={form} layout="vertical">
          <Form.Item
            name="inspection_type_id"
            label="Inspection Type"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              size="large"
              placeholder="Select"
              allowClear
              loading={queryInspectionType.isLoading}
              onChange={(val) => {
                setSelectedInspectionType(val)
                form.setFieldValue('template_id', null)
              }}
              notFoundContent={
                queryInspectionType.isLoading ? (
                  <Spin size="small" />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
            >
              {queryInspectionType.data?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.value?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="template_id"
            label="Inspection Template"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              size="large"
              placeholder="Select"
              allowClear
              notFoundContent={
                queryInspectionTemplate.isLoading ? (
                  <Spin size="small" />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
            >
              {queryInspectionTemplate.data?.data?.data?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="inspection_aql"
            label="Inspection AQL"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Select
              allowClear
              showSearch
              size="large"
              placeholder="Select AQL"
              filterOption={false}
              options={aqlOptions}
            />
          </Form.Item>

          {templateHasMeasurement && hasMeasurementAqlSetting && (
            <Form.Item name="measurement_aql" label="Measurement AQL">
              <Select
                allowClear
                showSearch
                size="large"
                placeholder="Select AQL"
                filterOption={false}
                options={measurementAqlOptions}
              />
            </Form.Item>
          )}

          <Form.Item name="planned_date" label="Planned Date">
            <DatePicker
              className="drawer-new-inspection__full-width"
              size="large"
              placeholder="Select date"
              format="DD MMM YYYY"
            />
          </Form.Item>

          {renderInspectorField()}

          <FileUploadField
            label="Documents"
            name="documents"
            required={false}
            allowLargeFile={true}
          />
        </Form>
      </div>

      <Row className="wrap-group-btn">
        <Button size="large" onClick={onClose} className="btn-cancel">
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          loading={createMutation.isLoading}
          disabled={isCreateDisabled}
          onClick={onSubmit}
          className="btn-save"
        >
          Create
        </Button>
      </Row>
    </Drawer>
  )
}

export default DrawerNewInspection
